import { createContext, useContext, useState, useMemo, useEffect, useRef, type ReactNode } from "react";
import { useQuery } from "convex/react";
import { convexApi } from "@/lib/convex-api";
import { useConvexStaff, shouldSkipConvexQueries, type ConvexStaffMember } from "@/hooks/use-convex-data";
import { useSite } from "@/lib/site-context";
import { useAuth } from "@/lib/auth-provider";
import { getUserStorage } from "@/lib/user-storage";
import { useRegisterLoadingState } from "@/lib/connection-recovery";

// Grace period before treating auth loss as a true logout (ms)
// This prevents staff data from being cleared on momentary auth flickers
const AUTH_LOGOUT_GRACE_PERIOD_MS = 3000;

export interface Staff {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  roles: string[];
  color: string;
  isActive: boolean;
  status?: string;
  joinedAt?: number;
  hasRosterAccess?: boolean;
  avatarFileId?: string;
  dateOfBirth?: number;
  shareBirthday?: boolean;
}

type ViewMode = "staff" | "manager";

interface StaffContextType {
  selectedStaff: Staff | null;
  setSelectedStaff: (staff: Staff | null) => void;
  clearSelection: () => void;
  allStaff: Staff[];
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  canAccessManagerView: boolean;
  isManager: boolean;
  isOwner: boolean;
  isLoading: boolean;
}

const StaffContext = createContext<StaffContextType | undefined>(undefined);

function mapConvexStaffToStaff(convexStaff: ConvexStaffMember[]): Staff[] {
  return convexStaff.map(s => ({
    id: s._id,
    name: s.name,
    email: s.email || "",
    phone: s.phone || "",
    role: s.roles?.[0] || "staff",
    roles: s.roles || [],
    color: s.color || "#6366f1",
    isActive: s.isActive ?? true,
    status: s.status || "active",
    joinedAt: s.createdAt,
    hasRosterAccess: s.hasRosterAccess,
    avatarFileId: s.avatarFileId,
    dateOfBirth: s.dateOfBirth,
    shareBirthday: s.shareBirthday,
  }));
}

export function StaffProvider({ children }: { children: ReactNode }) {
  const [selectedStaff, setSelectedStaffState] = useState<Staff | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const previousUserIdRef = useRef<string | null>(null);
  const previousSiteIdRef = useRef<string | null>(null);
  
  const { user, isAuthenticated, isDevBypassMode } = useAuth();
  const userId = user?.id || null;
  const userStorage = useMemo(() => getUserStorage(userId), [userId]);
  
  const [viewMode, setViewModeState] = useState<ViewMode>(() => userStorage.getViewMode());

  const setViewMode = (mode: ViewMode) => {
    setViewModeState(mode);
    userStorage.setViewMode(mode);
  };
  
  const { currentSite: site } = useSite();
  const siteId = site?._id || null;
  const convexStaffData = useConvexStaff(siteId);
  
  const allStaff = useMemo(() =>
    mapConvexStaffToStaff(convexStaffData || []), [convexStaffData]);

  // Server-side staff matching via getMyStaffForSite query.
  // This resolves google_/mobile userIds to kp_ Kinde IDs on the server,
  // which is more reliable than client-side matching.
  const skipQueries = shouldSkipConvexQueries();
  const serverMyStaff = useQuery(
    convexApi.staff.getMyStaffForSite,
    skipQueries || !siteId ? "skip" : { siteId }
  ) as ConvexStaffMember | null | undefined;

  // Find current user's staff record — prefer server-side match, then client-side fallback
  const userEmail = user?.email?.toLowerCase() || null;
  const myStaff = useMemo(() => {
    // Server-side match (handles google_ → kp_ userId resolution + email fallback)
    if (serverMyStaff) {
      return mapConvexStaffToStaff([serverMyStaff])[0] || null;
    }

    // If server query returned null (no match) or is still loading, try client-side fallback
    if (!convexStaffData) {
      return null;
    }

    // Try to find staff by userId first (production path)
    if (userId) {
      const myStaffData = convexStaffData.find(s => s.userId === userId);
      if (myStaffData) {
        return mapConvexStaffToStaff([myStaffData])[0] || null;
      }
    }

    // Fallback: match by email (handles google_ vs kp_ userId mismatch)
    if (userEmail) {
      const byEmail = convexStaffData.find(s => s.email?.toLowerCase() === userEmail);
      if (byEmail) {
        return mapConvexStaffToStaff([byEmail])[0] || null;
      }
    }

    // DEV BYPASS ONLY: If no staff found by userId, pick first staff with manager access
    // This allows testing manager features in dev mode without needing real userId links
    if (isDevBypassMode && convexStaffData.length > 0) {
      // Prefer staff with hasRosterAccess or manager role for testing manager features
      const managerStaff = convexStaffData.find(
        s => s.hasRosterAccess === true || s.roles?.includes("manager")
      );
      const devStaff = managerStaff || convexStaffData[0];
      console.log("[StaffContext] DEV MODE: Using fallback staff:", devStaff.name);
      return mapConvexStaffToStaff([devStaff])[0] || null;
    }

    return null;
  }, [serverMyStaff, convexStaffData, userId, userEmail, isDevBypassMode]);
  
  const isLoading = convexStaffData === undefined;
  
  // Register loading state for connection recovery monitoring
  useRegisterLoadingState("staff-context", isLoading);
  
  // Clear staff selection when user changes (cross-login protection)
  useEffect(() => {
    if (userId !== previousUserIdRef.current) {
      if (previousUserIdRef.current !== null) {
        console.log("[StaffContext] User changed, clearing staff selection");
        setSelectedStaffState(null);
        setViewModeState("staff");
        setHasInitialized(false);
      }
      previousUserIdRef.current = userId;
    }
  }, [userId]);
  
  // Clear staff selection when user logs out - with grace period for auth flickers
  // This prevents data loss during token refreshes or momentary auth state changes
  const logoutTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    if (!isAuthenticated && previousUserIdRef.current) {
      // Start a grace period timer - only clear if auth stays false
      console.log("[StaffContext] Auth state lost, starting grace period before clearing staff");
      
      logoutTimeoutRef.current = setTimeout(() => {
        console.log("[StaffContext] Grace period expired, clearing staff selection (true logout)");
        setSelectedStaffState(null);
        setViewModeState("staff");
        setHasInitialized(false);
      }, AUTH_LOGOUT_GRACE_PERIOD_MS);
    } else if (isAuthenticated && logoutTimeoutRef.current) {
      // Auth recovered before grace period ended - cancel the clear
      console.log("[StaffContext] Auth recovered, cancelling staff clear");
      clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
    }
    
    return () => {
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
      }
    };
  }, [isAuthenticated]);
  
  // Clear staff selection when site changes
  useEffect(() => {
    if (siteId !== previousSiteIdRef.current) {
      if (previousSiteIdRef.current !== null) {
        console.log("[StaffContext] Site changed, clearing staff selection");
        setSelectedStaffState(null);
        setHasInitialized(false);
      }
      previousSiteIdRef.current = siteId;
    }
  }, [siteId]);
  
  // WATERTIGHT: Use myStaff from server-side identity query instead of picking from allStaff
  // This ensures we always show the current user's staff record, not another user's
  useEffect(() => {
    if (!hasInitialized && myStaff && !selectedStaff) {
      console.log("[StaffContext] Initializing with watertight myStaff:", myStaff.name, myStaff.email);
      setSelectedStaffState(myStaff);
      setHasInitialized(true);
    }
  }, [myStaff, hasInitialized, selectedStaff]);
  
  // Sync selectedStaff with myStaff updates (for real-time profile changes)
  useEffect(() => {
    if (selectedStaff && myStaff) {
      // Only sync if this is the same staff record (matching ID)
      if (selectedStaff.id === myStaff.id && JSON.stringify(myStaff) !== JSON.stringify(selectedStaff)) {
        console.log("[StaffContext] Syncing selectedStaff with myStaff updates");
        setSelectedStaffState(myStaff);
      }
    }
  }, [myStaff, selectedStaff]);
  
  const canAccessManagerView = useMemo(() => {
    // Check hasRosterAccess field (set via staff management)
    // Fallback to manager role check for backwards compatibility
    return selectedStaff?.hasRosterAccess === true ||
           selectedStaff?.roles?.includes("manager") === true;
  }, [selectedStaff]);

  // Derive isManager/isOwner from site role (matches POS behavior)
  const userRole = site?.userRole;
  const isManager = userRole === "owner" || userRole === "manager" || canAccessManagerView;
  const isOwner = userRole === "owner";

  const handleSetSelectedStaff = (staff: Staff | null) => {
    setSelectedStaffState(staff);
    // Check hasRosterAccess OR manager role for access
    const hasAccess = staff?.hasRosterAccess === true || 
                      staff?.roles?.includes("manager") === true;
    if (!hasAccess && viewMode === "manager") {
      setViewModeState("staff");
      userStorage.setViewMode("staff");
    }
  };

  const clearSelection = () => {
    setSelectedStaffState(null);
    setViewModeState("staff");
    userStorage.setViewMode("staff");
  };
  
  return (
    <StaffContext.Provider value={{
      selectedStaff,
      setSelectedStaff: handleSetSelectedStaff,
      clearSelection,
      allStaff,
      viewMode,
      setViewMode,
      canAccessManagerView,
      isManager,
      isOwner,
      isLoading,
    }}>
      {children}
    </StaffContext.Provider>
  );
}

export function useStaff() {
  const context = useContext(StaffContext);
  if (context === undefined) {
    throw new Error("useStaff must be used within a StaffProvider");
  }
  return context;
}

export function useStaffSafe() {
  const context = useContext(StaffContext);
  return context || {
    selectedStaff: null,
    setSelectedStaff: () => {},
    clearSelection: () => {},
    allStaff: [],
    viewMode: "staff" as ViewMode,
    setViewMode: () => {},
    canAccessManagerView: false,
    isManager: false,
    isOwner: false,
    isLoading: false,
  };
}
